// Referral attribution — the capture half of the growth loop.
//
// Share links carry `?ref={referrerUid}&c={channel}` (see trackedUrl() in
// DatingTemplate.Clean.jsx). When someone lands via such a link we stash the
// referrer + channel locally (FIRST-TOUCH wins) so that whenever they later
// create a profile / join, we can attach `referredBy` and credit the referrer.
//
// Pure module: only touches window.location + localStorage. No Firebase here.

const REFERRAL_KEY = "lex_referral";
const TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

function safeParse(raw) {
  try {
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// Read a stored, non-expired referral, or null.
export function readPendingReferral() {
  if (typeof window === "undefined") return null;
  let rec;
  try {
    rec = safeParse(window.localStorage.getItem(REFERRAL_KEY));
  } catch {
    return null;
  }
  if (!rec || !rec.ref) return null;
  if (!rec.ts || Date.now() - Number(rec.ts) > TTL_MS) {
    clearPendingReferral();
    return null;
  }
  return rec;
}

export function clearPendingReferral() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(REFERRAL_KEY);
  } catch {
    /* ignore */
  }
}

// Read ?ref & ?c from the current URL. If present (and not a self-referral),
// stash first-touch to localStorage, then strip the params from the visible
// URL so they are never accidentally re-shared. `selfUid` is the currently
// logged-in user's uid (optional) — a self-referral is ignored.
//
// Returns the stored record (or the existing first-touch one), or null.
export function captureReferralFromUrl(selfUid = "") {
  if (typeof window === "undefined") return null;

  let params;
  try {
    params = new URLSearchParams(window.location.search || "");
  } catch {
    return readPendingReferral();
  }

  const ref = String(params.get("ref") || "").trim();
  const channel = String(params.get("c") || "").trim();

  if (!ref) return readPendingReferral();

  // strip ref/c from the URL regardless, so they don't get re-shared
  const strip = () => {
    try {
      params.delete("ref");
      params.delete("c");
      const qs = params.toString();
      const clean =
        window.location.pathname + (qs ? "?" + qs : "") + (window.location.hash || "");
      window.history.replaceState(null, "", clean);
    } catch {
      /* ignore */
    }
  };

  // ignore self-referral
  if (selfUid && ref === String(selfUid).trim()) {
    strip();
    return readPendingReferral();
  }

  // FIRST-TOUCH: don't overwrite an existing valid referral
  const existing = readPendingReferral();
  if (existing) {
    strip();
    return existing;
  }

  const rec = { ref, c: channel || "", ts: Date.now() };
  try {
    window.localStorage.setItem(REFERRAL_KEY, JSON.stringify(rec));
  } catch {
    /* ignore */
  }
  strip();
  return rec;
}

export { REFERRAL_KEY, TTL_MS };
